import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import { Row, Cell, Card, Icon, Section, Tabs } from "@sunwing/components";
import { RCLContext } from "../../components/RCL/RCLProvider";
import { RCL } from "../../components/RCL";
import { useRouting } from "../../hooks";
import { cloudinaryImage } from "../../utils/cloudinary";
import { Loader } from "../../components/Loader";
import moment from "moment";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useOktaAuth } from "@okta/okta-react";
import config from "../../app/config";
import { Link } from 'react-router-dom';
import { VWQIcon } from "@sunwing/icons";

let styles;

(async () => {
  if (config.isWestJetMode) {
    styles = await import("./DealsOffersVwq.module.scss");
  } else {
    styles = await import("./DealsOffers.module.scss");
  }
})();

const DealsOffers = () => {
  const { locale } = useContext(RCLContext);
  const { setCurrentRoute } = useRouting();
  const [tabIndex, setTabIndex] = useState("tabfly-0");
  const { oktaAuth } = useOktaAuth();
  const [dealsOffersInfo, setDealsOffersInfo] = useState(null);

  const getDealsOffersInfo = async () => {
    const query =`
    query dealsOffersData($locale: String, $skip:Int, $currentDate:DateTime, $brandCode:String!) {
      dealsOffersCollection(locale: $locale, limit:100, skip:$skip, where:{expiryDate_gte:$currentDate, brand: {brandCode:$brandCode} }) {
        items {
          sys {
            id
          }
          displayTitle
          b2BFile {
            url
            size
            description
          }
          b2CFile {
            url
            size
            description
          }
          startDate
          expiryDate
          category
          flyerImageAsset
          landingPage {
            sys {
              id
            }
            slug
            landingPageLinkText
          }
        }
      }
    }
  `;

  await fetch(config.resourceServer.contentfulApimAgentGqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
      },
      body: JSON.stringify({
        query: query,
        variables: { 
          locale: locale === "fr" ? locale : "en-CA", 
          "skip":0,
          "currentDate":(new Date()).toISOString().substring(0, 10),
          brandCode:config.brand ? config.brand.toUpperCase() : 'SWG'
        },
      }),
    })
      .then((response) => response.json())
      .then(({ data, errors }) => {
        if (errors) {
          console.error(errors);
        }else{
          setDealsOffersInfo(data?.dealsOffersCollection?.items);
        }
      });
  };

  const dealOfferCategories =
  dealsOffersInfo && !!dealsOffersInfo.length &&
  dealsOffersInfo
    .map((dealOffer) => dealOffer.category[0])
    .filter((value, index, self) => self.indexOf(value) === index);

  const dealOffersWithTabsData =
    dealOfferCategories &&
    dealOfferCategories?.sort((a, b) => -a.localeCompare(b)).map((category, index) => {
      return {
        key: `tabfly-${index}`,
        panel: (
          <>
            {!dealsOffersInfo && <Loader />}
            {dealsOffersInfo !== null && (
              <div>
                <h2>
                  {locale === "fr"
                    ? category.split("/")[1]
                    : category.split("/")[0]}
                </h2>
                <Row cols={3}>
                  {dealsOffersInfo
                    ?.slice()
                    .sort((a, b) => ((a.startDate !== null) && -a.startDate.localeCompare(b.startDate)))
                    .filter((dealOffer) => dealOffer.category[0] === category)
                    .map(
                      (dealOffer) =>                        
                        dealOffer?.flyerImageAsset !== null 
                        && ((dealOffer?.startDate==null) || ((dealOffer?.startDate !==null) && (moment().isAfter(dealOffer?.startDate))))
                        && dealOffer?.expiryDate!==null && moment().isBefore(dealOffer?.expiryDate) && (                          
                        <Cell key={dealOffer.displayTitle}>
                            <Card>
                              <Card.Image
                                className={styles.dealsOffersImage}
                                src={cloudinaryImage(
                                  dealOffer?.flyerImageAsset?.[0]?.public_id,
                                  {
                                    width: 747,
                                  }
                                )}
                                alt="placeholder alt"
                              />
                              <Card.Body>
                                <p className={ styles.dealsOffersPostDate }>
                                  {dealOffer.startDate ? dayjs(dealOffer.startDate, "YYYY-MM-D").format(
                                    "MMM D, YYYY"
                                  ) : <br/>}
                                </p>
                                <Card.Header
                                  as="p"
                                  size="p"
                                  weight="bold"
                                  className={styles.dealsOffersCardHeading}
                                >
                                  {dealOffer.displayTitle}
                                </Card.Header>
                                <div  className={styles.dealsOffersCardLinkContainer}>
                                  {dealOffer.b2BFile && dealOffer.b2BFile.url && 
                                    <div>
                                      <Card.Link
                                        className={ styles.dealsOffersLink}
                                        target="_blank"
                                        to={dealOffer.b2BFile.url}
                                      >
                                        <span><RCL searchKey="download-b2b-flyer" /></span>
                                        <VWQIcon
                                          className={styles.dealsOffersDownloadLink}
                                          name="arrow-right-micro"
                                          aria-hidden="true"
                                        />
                                      </Card.Link>
                                    </div>
                                  }
                                  {dealOffer.b2CFile && dealOffer.b2CFile.url && 
                                    <div>
                                      <Card.Link
                                        className={ styles.dealsOffersLink}
                                        target="_blank"
                                        to={dealOffer.b2CFile.url}
                                      >
                                        <span><RCL searchKey="download-b2c-flyer" /></span>
                                        <VWQIcon
                                          className={styles.dealsOffersDownloadLink}
                                          name="arrow-right-micro"
                                          aria-hidden="true"
                                        />
                                      </Card.Link>
                                    </div>
                                  }
                                  {dealOffer.landingPage && dealOffer?.landingPage?.sys.id && (
                                    <Link
                                      className={ styles.dealsOffersLandingPageLink}
                                      target="_blank"
                                      to={`/${locale}/LandingPage?brand=${config.brand}&slug=${dealOffer?.landingPage?.slug}`}
                                    >
                                      {dealOffer?.landingPage?.landingPageLinkText}
                                      <VWQIcon
                                        className={styles.dealsOffersLandingPageLinkIcon}
                                        name="arrow-right-micro"
                                        aria-hidden="true"
                                      />
                                    </Link>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Cell>
                        )
                    )}
                </Row>
              </div>
            )}
          </>
        ),
        toggle: {
          iconComponent: <Icon className="custom-icon" name="children" />,
          label: {
            heading:
              locale === "fr" ? category.split("/")[1] : category.split("/")[0],
          },
        },
      };
    });

  useEffect(() => {
    if (setCurrentRoute) {
      setCurrentRoute({
        en: { path: "/en/deals-offers" },
        fr: { path: "/fr/deals-offers" },
      });
    }
    getDealsOffersInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.dealsOffers}>
      <Helmet>
        <title>{`Agent Portal - ${RCL({ searchKey: "sales-deals-and-offers" })}`}</title>
      </Helmet>
      {!config.isWestJetMode && dealsOffersInfo && (
         <Breadcrumb pageSlug={"deals-offers"} locale={locale} />
      )}
      <Section
        title={RCL({ searchKey: "sales-deals-and-offers" })}
        titleAlignment="left"
        titleUppercase={false}
      >
        <p className={styles.dealsOffersDesc}>
         <RCL searchKey="deals_offers_desc" />
        </p>

        {dealOffersWithTabsData && (
          <Tabs
            activeKey={tabIndex}
            data={dealOffersWithTabsData}
            tabIconLeftComponent={<Icon name="arrow-left" />}
            tabIconRightComponent={<Icon name="arrow-right" />}
            theme="generic"
            customTabWrapperClass="tabsWrapper"
            customPanelClass="tabsPanel"
            customActiveClass="tabsActive"
            className={styles.dealsOffers}
          />
        )}
      </Section>
    </div>
  );
};

export default DealsOffers;
